import {Box} from "@mui/material";
import {useMemo, useRef, useState} from "react";

type CodeLanguage =
    | "PYTHON"
    | "GO"
    | "JAVA"
    | "JAVA_CLASS"
    | "JAVA_METHOD"
    | "GO_STRUCT"
    | "python"
    | "go"
    | "java"
    | string;

type Props = {
    value: string;
    onChange: (value: string) => void;
    language?: CodeLanguage;
    height?: number;
};

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

const normalizeLanguage = (language?: string): "java" | "go" | "python" => {
    switch ((language || "").toUpperCase()) {
        case "GO":
        case "GO_STRUCT":
            return "go";
        case "PYTHON":
            return "python";
        case "JAVA":
        case "JAVA_CLASS":
        case "JAVA_METHOD":
        default:
            return "java";
    }
};

const getKeywords = (language: "java" | "go" | "python") => {
    if (language === "go") {
        return [
            "package", "import", "func", "var", "const", "type", "struct",
            "interface", "return", "if", "else", "for", "range", "switch",
            "case", "default", "break", "continue", "go", "defer", "map",
            "chan", "select", "true", "false", "nil",
        ];
    }

    if (language === "python") {
        return [
            "def", "class", "import", "from", "as", "return", "if", "elif",
            "else", "for", "while", "in", "break", "continue", "pass",
            "try", "except", "finally", "raise", "with", "lambda", "yield",
            "global", "nonlocal", "assert", "del", "True", "False", "None",
            "and", "or", "not", "is",
        ];
    }

    return [
        "public", "import", "private", "protected", "class", "interface", "enum",
        "record", "static", "final", "void", "int", "long", "double",
        "float", "boolean", "char", "String", "new", "return", "if",
        "else", "for", "while", "do", "switch", "case", "default",
        "break", "continue", "try", "catch", "finally", "throw",
        "throws", "extends", "implements", "this", "super", "true",
        "false", "null",
    ];
};

const wrap = (content: string, color: string, fontWeight?: number) => {
    return `<span style="color:${color}${fontWeight ? `;font-weight:${fontWeight}` : ""}">${escapeHtml(content)}</span>`;
};

const highlightCode = (code: string, rawLanguage: string) => {
    const language = normalizeLanguage(rawLanguage);
    const keywords = new Set(getKeywords(language));

    let result = "";
    let i = 0;

    while (i < code.length) {
        const current = code[i];
        const next = code[i + 1];

        if (language === "python" && current === "#") {
            let end = i;

            while (end < code.length && code[end] !== "\n") {
                end++;
            }

            result += wrap(code.slice(i, end), "rgb(100, 116, 139)");
            i = end;
            continue;
        }

        if (language !== "python" && current === "/" && next === "/") {
            let end = i;

            while (end < code.length && code[end] !== "\n") {
                end++;
            }

            result += wrap(code.slice(i, end), "rgb(100, 116, 139)");
            i = end;
            continue;
        }

        if (current === `"` || current === `'`) {
            const quote = current;
            let end = i + 1;
            let escaped = false;

            while (end < code.length) {
                const char = code[end];

                if (escaped) {
                    escaped = false;
                    end++;
                    continue;
                }

                if (char === "\\") {
                    escaped = true;
                    end++;
                    continue;
                }

                if (char === quote) {
                    end++;
                    break;
                }

                end++;
            }

            result += wrap(code.slice(i, end), "rgb(134, 239, 172)");
            i = end;
            continue;
        }

        const numberMatch = code.slice(i).match(/^\d+/);
        if (numberMatch) {
            result += wrap(numberMatch[0], "rgb(251, 191, 36)");
            i += numberMatch[0].length;
            continue;
        }

        const wordMatch = code.slice(i).match(/^[A-Za-z_][A-Za-z0-9_]*/);
        if (wordMatch) {
            const word = wordMatch[0];

            if (keywords.has(word)) {
                result += wrap(word, "rgb(96, 165, 250)", 600);
            } else {
                result += escapeHtml(word);
            }

            i += word.length;
            continue;
        }

        result += escapeHtml(current);
        i++;
    }

    return result || " ";
};

const CodeTextarea = ({value, onChange, language = "JAVA", height = 400}: Props) => {
    const [scrollTop, setScrollTop] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const lineCount = useMemo(() => {
        return Math.max(value.split("\n").length, 1);
    }, [value]);

    const highlighted = useMemo(() => {
        return highlightCode(value, language);
    }, [value, language]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Tab") {
            e.preventDefault();

            const textarea = e.currentTarget;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;

            const newValue =
                value.substring(0, start) + "    " + value.substring(end);

            onChange(newValue);

            setTimeout(() => {
                textarea.selectionStart = textarea.selectionEnd = start + 4;
            });
        }
    };

    return (
        <Box
            sx={{
                height,
                display: "flex",
                backgroundColor: "#0f172a",
                color: "#e5e7eb",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    width: 56,
                    py: 2,
                    px: 1,
                    backgroundColor: "#111827",
                    color: "#64748b",
                    textAlign: "right",
                    userSelect: "none",
                    overflow: "hidden",
                    fontSize: 14,
                    lineHeight: "22px",
                }}
            >
                <Box sx={{transform: `translateY(-${scrollTop}px)`}}>
                    {Array.from({length: lineCount}).map((_, index) => (
                        <Box key={index} sx={{fontSize: 14, lineHeight: "22px"}}>
                            {index + 1}
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box sx={{position: "relative", flex: 1, height: "100%"}}>
                <Box
                    component="pre"
                    aria-hidden
                    dangerouslySetInnerHTML={{__html: highlighted}}
                    sx={{
                        position: "absolute",
                        inset: 0,
                        m: 0,
                        p: 2,
                        transform: `translate(${-scrollLeft}px, ${-scrollTop}px)`,
                        color: "#e5e7eb",
                        fontFamily: "inherit",
                        fontSize: 14,
                        lineHeight: "22px",
                        letterSpacing: 0,
                        fontVariantLigatures: "none",
                        whiteSpace: "pre",
                        pointerEvents: "none",
                    }}
                />

                <Box
                    ref={textareaRef}
                    component="textarea"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onScroll={(e) => {
                        setScrollTop(e.currentTarget.scrollTop);
                        setScrollLeft(e.currentTarget.scrollLeft);
                    }}
                    spellCheck={false}
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        resize: "none",
                        outline: "none",
                        border: "none",
                        p: 2,
                        backgroundColor: "transparent",
                        color: "rgba(229,231,235,0.01)",
                        caretColor: "#e5e7eb",
                        fontFamily: "inherit",
                        fontSize: 14,
                        lineHeight: "22px",
                        letterSpacing: 0,
                        fontVariantLigatures: "none",
                        tabSize: 4,
                        overflow: "auto",
                        whiteSpace: "pre",
                        "&::selection": {
                            backgroundColor: "rgba(59, 130, 246, 0.45)",
                            color: "transparent",
                        },
                    }}
                />
            </Box>
        </Box>
    );
};

export default CodeTextarea;